/**
 * The single source of truth for what each plan unlocks.
 *
 * Everything that gates a feature — `useFeatureGating`, the URL sanitizer in
 * `url-state.ts`, and the pricing copy — derives from the tables here, so moving
 * a feature between tiers is a one-word edit rather than a hunt through the app.
 *
 * This is a *client-side* gate, not enforcement: the parcel data is already in
 * the browser. Statewide search is the one premium feature enforced server-side
 * (`workers/state-search`), and it keeps its own copy of the entitlement rule.
 */

export const PLAN_RANK = { free: 0, lite: 1, pro: 2 } as const;

export type Plan = keyof typeof PLAN_RANK;

export type Feature =
  | "exportCsv"
  | "exportGpx"
  | "paginateResults"
  | "filterAcres"
  | "filterValue"
  | "filterRadius"
  | "searchLeadTerms"
  | "searchMailingStatewide"
  | "saveProperties"
  | "ownershipAlerts"
  | "aiAssistantSearch"
  | "aiSalesData"
  | "mapLayers";

/**
 * Minimum plan each feature requires.
 *
 * `saveProperties` is open to every signed-in account, capped by plan
 * (`savedPropertyLimit`): Free keeps 5 parcels, Lite 100, Pro any number.
 * Sale alerts on them are Pro (`ownershipAlerts`) and not built yet. It was Pro until 2026-10, and since Pro is not on
 * sale one account had ever saved anything; a reason to make an account on the
 * first visit, and to come back, is worth more than the gate was. The
 * database enforces the same cap (`saved_property_limit` in the
 * `20261019120000_saved_properties_free_tier` migration).
 *
 * `aiAssistantSearch` is the statewide search reached through ChatGPT or Claude
 * (`workers/mcp`) — the same search Lite already gets on `/map`, plus the
 * ownership totals and radius search that answer the same questions.
 * `aiSalesData` is RETR sales (search, totals, a parcel's history) there.
 * `mapLayers` is the per-parcel map layers — waterfront, wetlands, floodplain,
 * the proposed 765-kV line — wherever they are offered: as filters and parcel
 * facts in the AI tools, and on the county map. One feature, so a Pro
 * subscriber gets the same layers in both places and the price is one decision.
 * `searchLeadTerms` is a statewide owner search for a category rather than a
 * name — "deceased", "heirs", "estate of" (`leadTermsIn` in
 * statewide-search.ts). That is a lead list, which is what a professional pays
 * for; a person looking up one owner never needs those words.
 * `searchMailingStatewide` is "other parcels mailed to this owner" across all
 * 72 counties (a `mailOf` search): one owner's whole portfolio, under every
 * LLC and trust name, which is research a professional pays for. Free keeps
 * the same search inside one county (the county's address box), and is told
 * how many parcels and counties the statewide one would list
 * (`mailing_locked`, 2026-10). In the 30 days before, 234 people used the
 * mailing search and 2 of them bought Lite, about ten times the site's rate.
 */
export const FEATURE_MIN_PLAN: Record<Feature, Plan> = {
  exportCsv: "lite",
  exportGpx: "lite",
  paginateResults: "lite",
  filterAcres: "lite",
  filterValue: "lite",
  filterRadius: "lite",
  searchLeadTerms: "lite",
  searchMailingStatewide: "lite",
  saveProperties: "free",
  ownershipAlerts: "pro",
  aiAssistantSearch: "lite",
  aiSalesData: "pro",
  mapLayers: "pro",
};

/**
 * How many parcels each plan may save; Pro has no cap. The database holds the
 * same numbers (`public.saved_property_limit`), and a test reads both.
 */
export const FREE_SAVED_PROPERTY_LIMIT = 5;
export const LITE_SAVED_PROPERTY_LIMIT = 100;

/** The saved-property cap for a plan, or null for no cap. */
export function savedPropertyLimit(plan: Plan): number | null {
  if (plan === "free") return FREE_SAVED_PROPERTY_LIMIT;
  if (plan === "lite") return LITE_SAVED_PROPERTY_LIMIT;
  return null;
}

export function planAllows(plan: Plan, feature: Feature): boolean {
  return PLAN_RANK[plan] >= PLAN_RANK[FEATURE_MIN_PLAN[feature]];
}

/** The subscription fields `resolvePlan` needs. Kept structural so both the app's
 *  `Profile` and a bare row from PostgREST satisfy it. */
export interface PlanBearingProfile {
  manual_premium?: boolean | null;
  subscription_tier?: string | null;
  subscription_status?: string | null;
  /** "Paid through" — the authority on entitlement. */
  subscription_end_at?: string | null;
}

/**
 * Map a profile onto a plan.
 *
 * **Entitlement is date-based.** `subscription_end_at` means "paid through", and
 * `subscription_status` is informational only. This matters because Stripe marks
 * a subscription as cancelling the moment the customer clicks cancel — while it
 * is still active and fully paid — so requiring `status === "active"` revoked
 * access instantly, contradicting the cancel-any-time promise the pricing page,
 * upgrade modal and account settings all make. The date moves only on a paid
 * invoice (`paidThroughOf` in the edge functions' `_shared/billing.ts`), so a
 * renewal whose card fails (`past_due`) ends access at the renewal date, and a
 * retry that succeeds restores it.
 *
 * `profiles` has no `plan` column yet — `subscription_tier` is a *billing
 * interval* (monthly/yearly/lifetime), not a product level. When the
 * `plan` / `billing_interval` split lands, this function is the only thing that
 * changes; every caller already speaks in plans.
 *
 * `manual_premium` resolves to `pro` deliberately: it is the owner/tester/comp
 * flag, so it must not lose access to features that move up a tier.
 *
 * The statewide-search worker keeps the only other copy of this rule
 * (`workers/state-search/src/index.ts`) — change them together.
 */
export function resolvePlan(profile: PlanBearingProfile | null | undefined): Plan {
  if (!profile) return "free";
  if (profile.manual_premium) return "pro";
  if (profile.subscription_tier === "lifetime") return "lite";

  const paidThrough = profile.subscription_end_at ? new Date(profile.subscription_end_at) : null;
  if (paidThrough && !Number.isNaN(paidThrough.getTime()) && paidThrough > new Date()) {
    return "lite";
  }

  return "free";
}

/** How a subscription's end date should be described to the person who owns it. */
export interface RenewalDescription {
  /** Label for the date, e.g. "Renews on" or "Access until". Null when no date applies. */
  dateLabel: string | null;
  /** True only when money will actually be taken again on that date. */
  willRenew: boolean;
  /** A sentence explaining the state, or null when "Renews on <date>" says it all. */
  note: string | null;
}

/**
 * Describe whether a subscription renews, in the words the customer needs.
 *
 * This exists because the two places that showed a billing date disagreed with
 * the database. `AccountMenu` printed a bare "Next billing" for every status,
 * so a customer who had *canceled* was told their card would be charged again
 * — the single most alarming thing a billing UI can get wrong. `AccountSettings`
 * special-cased only `cancelled`, which meant `cancelling` — the status a
 * cancellation through the Stripe portal actually produces, and therefore the
 * common case — also read as "Next billing date".
 *
 * Both now derive from here. The rule is: only say a date is a billing date
 * when a charge will really happen on it.
 */
export function describeRenewal(profile: PlanBearingProfile | null | undefined): RenewalDescription {
  if (!profile) return { dateLabel: null, willRenew: false, note: null };

  if (profile.manual_premium) {
    return { dateLabel: null, willRenew: false, note: "Complimentary access — no billing, no expiry." };
  }

  if (profile.subscription_tier === "lifetime") {
    return { dateLabel: null, willRenew: false, note: "Lifetime access — never expires." };
  }

  // A renewal past its grace period has lost access, but it is not over:
  // Stripe is still retrying the card, and a retry that succeeds restores Lite.
  // "Ended" with "Renew Lite" invited a second checkout that the retry would
  // then double-charge.
  if (isLapsedSubscription(profile) && profile.subscription_status === "past_due") {
    return {
      dateLabel: "Paid through",
      willRenew: false,
      note: "We couldn't process your renewal payment, so Lite is paused while we retry. Updating your payment method will restore it.",
    };
  }

  // Past its paid-through date the subscription is over, whatever the status
  // says: "Access until <a past date>" with "you keep full access" told a
  // lapsed subscriber they still had Lite.
  if (isLapsedSubscription(profile)) {
    return { dateLabel: "Ended on", willRenew: false, note: "Your Lite subscription has ended." };
  }

  switch (profile.subscription_status) {
    case "cancelling":
    case "cancelled":
    case "expired":
      return {
        dateLabel: "Access until",
        willRenew: false,
        note: "Canceled — this won't renew. You keep full access until the date above.",
      };

    case "past_due":
      return {
        dateLabel: "Access until",
        willRenew: false,
        note: "We couldn't process your last payment. Access continues while we retry — updating your payment method will clear this.",
      };

    case "active":
      return { dateLabel: "Renews on", willRenew: true, note: null };

    // An unrecognised status must not claim a charge is coming.
    default:
      return { dateLabel: "Access until", willRenew: false, note: null };
  }
}

/**
 * A Lite subscription (`monthly`/`yearly`) whose paid-through date has passed.
 * The webhook leaves `subscription_tier` at the old interval after a
 * subscription ends, so the tier alone says nothing about access; the plan
 * decides, through `resolvePlan`.
 */
function isLapsedSubscription(profile: PlanBearingProfile): boolean {
  return (
    !profile.manual_premium &&
    (profile.subscription_tier === "monthly" || profile.subscription_tier === "yearly") &&
    resolvePlan(profile) === "free"
  );
}

/** `PlanBearingProfile` plus the one billing field the account surfaces read. */
export interface BillingProfile extends PlanBearingProfile {
  /** Set by checkout and the webhook; never cleared when a subscription ends. */
  stripe_customer_id?: string | null;
}

/** What the account surfaces show for a plan, and which billing actions they offer. */
export interface AccountPlanView {
  plan: Plan;
  /** "Free", "Lite (yearly)", "Pro (complimentary)"... */
  label: string;
  /** Opens the pricing modal (checkout), with its button text; null when none applies. */
  checkout: { kind: "upgrade" | "renew" | "resubscribe"; label: string } | null;
  /** Opens the Stripe customer portal, with its button text; null when there is nothing there. */
  portal: { kind: "manage" | "invoices"; label: string } | null;
}

const PLAN_NAME: Record<Plan, string> = { free: "Free", lite: "Lite", pro: "Pro" };

/**
 * The plan label and billing actions for the account menu and the Account
 * page, from the one plan rule (`resolvePlan`).
 *
 * Both surfaces used to label by `subscription_tier`, a billing interval the
 * webhook leaves at `yearly` after a subscription ends, and offered the portal
 * to anything `isPremium`. So a lapsed subscriber read "Lite (yearly)" while
 * gated as Free, and a `manual_premium` comp read "Free" and was sent to a
 * portal that has no Stripe customer for them (an error toast).
 *
 * - Comp: its plan, "(complimentary)", no checkout. The portal only when
 *   `stripe_customer_id` says there is a Stripe customer: "manage" while a
 *   subscription is still live (a comp can also be paying), else invoices.
 * - Lifetime: Lite, invoices only.
 * - Lite renewing, or `past_due` (fixing the card is what clears it, and a
 *   second checkout would be refused while the account still has Lite): the
 *   portal. Still `past_due` past the grace period it resolves to Free, but
 *   Stripe is still retrying, so it gets the portal and no checkout: a
 *   checkout there would be double-charged by the retry that succeeds.
 * - Lite canceled but paid through (`cancelling`/`cancelled`): resubscribe,
 *   plus invoices. `expired` (an immediate cancel) gets invoices only:
 *   `create-checkout` refuses it until the date passes, because `v_user_plan`
 *   counts only `cancelling`/`cancelled` as churning.
 * - Lapsed: Free, with "Renew". The portal finds the customer by email, and
 *   `stripe_customer_id` is the client's only evidence that one exists, so
 *   past invoices are offered only when it is set.
 * - Free: upgrade.
 */
export function describeAccountPlan(profile: BillingProfile | null | undefined): AccountPlanView {
  const plan = resolvePlan(profile);
  const upgrade = { kind: "upgrade", label: "Upgrade to Lite — $10/yr" } as const;

  if (!profile) return { plan, label: PLAN_NAME[plan], checkout: upgrade, portal: null };

  const manage = { kind: "manage", label: "Manage Subscription & Billing" } as const;
  const status = profile.subscription_status;

  if (profile.manual_premium) {
    const live = profile.subscription_tier !== "lifetime" && (status === "active" || status === "past_due");
    const portal = !profile.stripe_customer_id
      ? null
      : live
        ? manage
        : ({ kind: "invoices", label: "Past invoices" } as const);
    return { plan, label: `${PLAN_NAME[plan]} (complimentary)`, checkout: null, portal };
  }

  const invoices = { kind: "invoices", label: "Payment method & invoices" } as const;

  if (profile.subscription_tier === "lifetime") {
    return { plan, label: "Lite (lifetime)", checkout: null, portal: invoices };
  }

  if (plan === "free") {
    if (isLapsedSubscription(profile) && status === "past_due") {
      return {
        plan,
        label: PLAN_NAME.free,
        checkout: null,
        portal: { kind: "manage", label: "Update payment method" },
      };
    }
    if (isLapsedSubscription(profile)) {
      return {
        plan,
        label: PLAN_NAME.free,
        checkout: { kind: "renew", label: "Renew Lite — $10/yr" },
        portal: profile.stripe_customer_id ? { kind: "invoices", label: "Past invoices" } : null,
      };
    }
    return { plan, label: PLAN_NAME.free, checkout: upgrade, portal: null };
  }

  const tier = profile.subscription_tier;
  const label =
    tier === "monthly" || tier === "yearly" ? `${PLAN_NAME[plan]} (${tier})` : PLAN_NAME[plan];
  if (status === "cancelling" || status === "cancelled") {
    return { plan, label, checkout: { kind: "resubscribe", label: "Resubscribe — $10/yr" }, portal: invoices };
  }
  if (status === "expired") {
    return { plan, label, checkout: null, portal: invoices };
  }
  return { plan, label, checkout: null, portal: manage };
}

/**
 * A group of URL params that together express one gated filter.
 *
 * Canonical spellings only. Legacy snake_case params (`acres_min`) are rewritten
 * to these by `normalizeUrlParams` before the strip runs, so there is exactly one
 * vocabulary to gate — an alias that never reached this list would be an open
 * bypass, which is why normalization has to come first.
 */
export interface GatedFilterGroup {
  feature: Feature;
  /** Shown to the user when the filter is stripped out of a shared link. */
  label: string;
  params: readonly string[];
}

export const GATED_FILTER_GROUPS: readonly GatedFilterGroup[] = [
  {
    feature: "filterAcres",
    label: "Acreage range",
    params: ["minAcres", "maxAcres"],
  },
  {
    feature: "filterValue",
    label: "Market value range",
    params: ["minValue", "maxValue"],
  },
  {
    feature: "filterRadius",
    label: "Search radius",
    params: ["radiusLat", "radiusLng", "radiusMiles"],
  },
  {
    // `LAYER_PARAM_KEYS` in parcel-layers.ts, spelled out because the
    // statewide-search worker imports this file and not that one. A test
    // holds the two lists equal.
    feature: "mapLayers",
    label: "Water and environment filtering",
    params: ["water", "frontage", "waterBody", "flood", "wetland", "wetlandPct", "tx765"],
  },
] as const;
